<script>
import Layout from '@layouts/default'
import UserLogin from '@components/user-login'
import UserRegistration from '@components/user-registration'
import CheckoutCart from '@components/checkout-cart'

import {
  orderMethods,
  authComputed,
  referralMethods,
  vhrMethods,
  isTokenValid,
} from '@state/helpers'
import {
  required,
  minLength,
  maxLength,
  numeric,
} from 'vuelidate/lib/validators'
import {
  creditCardValidator,
  notNull,
  aphaSpace,
  usaCanZipValidator,
} from '@utils/validators'
import { usaCanStates, monthsInYear, canadianValues } from '@utils/appConstants'
import { createCardYearOptions } from '@utils/dateUtils'
import formatDate from '@utils/format-date'
import _ from 'lodash'
import VehicleSearchSummary from '@components/vehicle-search-summary'
// import liveChat from '@components/live-chat'

export default {
  components: {
    Layout,
    UserLogin,
    UserRegistration,
    CheckoutCart,
    VehicleSearchSummary,
  },
  data() {
    return {
      currentUserData: _.get(this, '$store.state.auth.currentUser'),
      isLoggedIn: false,
      emailUpdates: _.get(this, '$store.state.auth.currentUser.emailOptIn'),
      customerType: 'new',
      paymentType: 'card',
      firstName: _.get(this, '$store.state.auth.currentUser.firstName', ''),
      lastName: _.get(this, '$store.state.auth.currentUser.lastName', ''),
      address: '',
      apartmentNo: '',
      city: '',
      state: null,
      zipCode: '',
      creditCardType: null,
      creditCardNumber: '',
      expirationMonth: null,
      expirationYear: null,
      securityCode: '',
      products: null,
      stateOptions: usaCanStates,
      monthOptions: monthsInYear,
      yearOptions: createCardYearOptions(
        new Date().getFullYear(),
        new Date().getFullYear() + 10
      ),
      creditCardTypes: [
        { text: 'Select', value: null, disabled: true },
        { text: 'Visa', value: 'VISA' },
        { text: 'Mastercard', value: 'MASTERCARD' },
        { text: 'Discover', value: 'DISCOVER' },
        { text: 'American Express', value: 'AMERICAN_EXPRESS' },
      ],
      tryingToCheckout: false,
      wasValidated: false,
      checkoutError: false,
      checkoutComplete: false,
      voucher: undefined,
      voucherError: false,
      voucherIsValid: false,
      wasVoucherValidated: false,
      voucherCheckoutError: false,
      voucherCheckoutComplete: false,
      checkoutResponse: {},
      maxlengthCC: 16,
      maxlengthSC: 4,
      activeVinSearch: _.get(this, '$store.state.vhr.vehicle.vin'),
      product: _.get(this, '$store.state.customer.cart'),
      vin: _.get(this, '$store.state.vhr.vehicle.vin'),
      siteID: _.get(this, '$store.state.referrer.referrerData.siteID', '0'),
    }
  },
  validations: {
    firstName: {
      required,
      maxLength: maxLength(20),
      minLength: minLength(2),
    },
    lastName: {
      required,
      maxLength: maxLength(20),
      minLength: minLength(2),
    },
    address: {
      required,
      maxLength: maxLength(32),
    },
    apartmentNo: {
      maxLength: maxLength(32),
    },
    city: {
      required,
      aphaSpace,
      maxLength: maxLength(20),
    },
    state: {
      required,
      notNull,
    },
    zipCode: {
      required,
      usaCanZipValidator,
    },
    creditCardType: {
      required,
    },
    creditCardNumber: {
      required,
      creditCardValidator,
      minLength: minLength(15),
    },
    expirationMonth: {
      required,
    },
    expirationYear: {
      required,
    },
    securityCode: {
      required,
      maxLength: maxLength(4),
      minLength: minLength(3),
      numeric,
    },
    emailUpdates: {},
  },
  page: {
    title: 'Checkout',
    meta: [{ name: 'description', content: 'Checkout' }],
  },
  computed: {
    voucherEnabled() {
      return _.get(this, '$store.state.referrer.referrerData.voucherEnabled')
    },
    paypalCancelError() {
      return _.get(this, '$route.query.error') === 'cancelPaypal'
        ? 'Failed to place the order.'
        : undefined
    },
  },
  created() {
    this.wasValidated = false
  },
  mounted() {
    // Watch for user login and set the emailUpdates (optin) to user value
    this.$store.subscribe((mutation, state) => {
      if (
        mutation.type === 'auth/SET_CURRENT_USER' &&
        state.auth.currentUser !== null
      ) {
        this.isLoggedIn = true
        this.emailUpdates = state.auth.currentUser.emailOptIn
        this.currentUserData = _.get(this, '$store.state.auth.currentUser')
        // Pre-populate form
        this.firstName = this.currentUserData.firstName
        this.lastName = this.currentUserData.lastName
      } else if (
        mutation.type === 'customer/UPDATE_CART' &&
        this.checkoutComplete
      ) {
        // If the user adds an upsell to cart after making a purchase, reset the values
        this.paymentType = 'card'
        this.firstName = ''
        this.lastName = ''
        this.address = ''
        this.apartmentNo = ''
        this.city = ''
        this.state = null
        this.zipCode = ''
        this.creditCardType = null
        this.creditCardNumber = ''
        this.expirationMonth = null
        this.expirationYear = null
        this.securityCode = ''
        this.wasValidated = false
        this.checkoutError = false
        this.checkoutComplete = false
      }
    })

    const items = [
      {
        item_id: this.product.code,
        item_name: this.product.name,
        price: parseFloat(this.product.price),
        currency: 'USD',
        quantity: 1,
      },
    ]

    if (this.$gtag) {
      this.$gtag.event('begin_checkout', {
        currency: 'USD',
        value: parseFloat(this.product.price),
        items: items,
      })
    }
  },
  methods: {
    ...orderMethods,
    ...authComputed,
    ...referralMethods,
    ...vhrMethods,
    setCheckoutCompletion() {
      // TODO: remove legacy code once Analytics 360 is dead
      this.$ga.set('dimension4', this.currentUserData.id.toString())
      this.$ga.set('dimension5', formatDate(new Date(), 'MM/DD/YYYY HH:mm:ss'))
      this.$ga.ecommerce.addTransaction({
        id: this.checkoutResponse.transactionId, // Transaction ID. Required.
        affiliation: 'autocheck.com', // Affiliation or store name.
        revenue: this.checkoutResponse.orderTotal, // Grand Total.
        shipping: 0, // Shipping.
        tax: this.checkoutResponse.tax, // Tax.
      })

      this.$ga.ecommerce.addItem({
        id: this.checkoutResponse.transactionId, // Transaction ID. Required.
        sku: this.checkoutResponse.code, // SKU/code.
        name: this.checkoutResponse.name,
        price: parseFloat(this.checkoutResponse.price),
        quantity: 1,
      })

      this.$ga.ecommerce.send()

      this.$gtag.event('capture_customer_id', {
        UniqueCustomerID: this.currentUserData.id.toString(),
        UniqueCustomerIDPurchaseTime: formatDate(
          new Date(),
          'MM/DD/YYYY HH:mm:ss'
        ),
      })

      // GA4 purchase
      const items = [
        {
          item_id: this.checkoutResponse.code,
          item_name: this.checkoutResponse.name,
          price: parseFloat(this.checkoutResponse.price),
          currency: 'USD',
          quantity: 1,
        },
      ]

      this.$gtag.purchase({
        transaction_id: this.checkoutResponse.transactionId,
        affiliation: 'autocheck.com',
        value: this.checkoutResponse.orderTotal,
        shipping: 0,
        tax: this.checkoutResponse.tax,
        currency: 'USD',
        items: items,
      })
    },
    tryToCheckout() {
      this.$v.$touch()

      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      if (this.$v.$invalid) {
        this.wasValidated = true
      } else {
        const items = [
          {
            item_id: this.product.code,
            item_name: this.product.name,
            price: parseFloat(this.product.price),
            currency: 'USD',
            quantity: 1,
          },
        ]

        if (this.$gtag) {
          this.$gtag.event('add_payment_info', {
            currency: 'USD',
            value: parseFloat(this.product.price),
            payment_type: 'Credit or Debit Card',
            items: items,
          })
        }

        this.tryingToCheckout = true
        return this.checkout({
          customerEmail: this.currentUserData.email,
          productPrice: parseFloat(this.product.price),
          currency: 'USD',
          productName: this.product.name,
          productCode: this.product.code,
          upsellTypeCode: this.product.upsellTypeDesc,
          vin: this.vin,
          billingAddress: {
            firstName: this.firstName,
            lastName: this.lastName,
            address1: this.address,
            address2: this.apartmentNo,
            city: this.city,
            state: this.state,
            postalCode: this.zipCode,
            country: _.find(canadianValues, this.state) ? 'CAN' : 'USA',
          },
          card: {
            cardType: this.creditCardType,
            ccNumber: this.creditCardNumber,
            expirationMonth: parseInt(this.expirationMonth, 10),
            expirationYear: this.expirationYear,
            securityCode: this.securityCode,
          },
          emailOptIn: this.emailUpdates,
          siteId: this.siteID,
        })
          .then((response) => {
            this.checkoutResponse = response
            this.tryingToCheckout = false
            this.checkoutComplete = true
            this.setCheckoutCompletion()
            // Clear session storage and revert to siteID 0 after checkout completion
            // MS160AS comment below line out.  Business rules changed; so the
            // user will not switch to an organic user after puchase.
            // It will retrieve products for the current siteID
            // This is for ticket ACATB - 5177 - 11/15/2019
            // sessionStorage.clear()
            this.getData(this.siteID)
              .then((response) => {
                this.getProducts()
                  .then((response) => {
                    this.products = response
                  })
                  .catch((error) => {
                    this.productError = error
                  })
              })
              .catch((error) => {
                this.productError = error
              })
          })
          .catch((error) => {
            this.tryingToCheckout = false
            this.checkoutError = error
            this.creditCardNumber = ''
            this.securityCode = ''
          })
      }
    },
    tryToCheckoutVoucher() {
      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      return this.checkoutVoucher({
        customerEmail: this.currentUserData.email,
        currency: 'USD',
        billingAddress: {
          firstName: this.currentUserData.firstName,
          lastName: this.currentUserData.lastName,
          streetName: this.address,
          town: this.city,
          name: this.currentUserData.firstName,
          region: this.state,
          postalCode: this.zipCode,
          country: _.find(canadianValues, this.state) ? 'CAN' : 'USA',
          email: this.currentUserData.email,
        },
        emailOptIn: this.emailUpdates,
        tax: 0,
        productPrice: 0,
        totalPrice: 0,
        productCode: this.product.code,
        voucherCode: this.voucher,
        productName: this.product.name,
        siteId: this.siteID,
        upsellTypeCode: this.product.upsellTypeDesc,
        vin: this.vin,
      })
        .then((response) => {
          this.checkoutResponse = response
          this.tryingToCheckout = false
          this.checkoutComplete = true
          this.voucherCheckoutComplete = true
          this.setCheckoutCompletion()
        })
        .catch((error) => {
          this.tryingToCheckout = false
          this.checkoutError = error
        })
    },
    tryToCheckoutPaypal() {
      this.tryingToCheckout = true

      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      const items = [
        {
          item_id: this.product.code,
          item_name: this.product.name,
          price: parseFloat(this.product.price),
          currency: 'USD',
          quantity: 1,
        },
      ]

      if (this.$gtag) {
        this.$gtag.event('add_payment_info', {
          currency: 'USD',
          value: parseFloat(this.product.price),
          payment_type: 'PayPal',
          items: items,
        })
      }

      return this.checkoutPaypal({
        customerEmail: this.currentUserData.email,
        productPrice: parseFloat(this.product.price),
        currency: 'USD',
        emailOptIn: this.emailUpdates,
        productCode: this.product.code,
        productName: this.product.name,
        upsellTypeCode: this.product.upsellTypeDesc,
        vin: this.vin,
      })
        .then((response) => {
          window.location = response
          this.checkoutResponse = response
          this.tryingToCheckout = false
        })
        .catch((error) => {
          this.tryingToCheckout = false
          this.checkoutError = error
        })
    },
    showModal() {
      this.$refs.termsModal.show()
    },
    tryToProcessVoucher() {
      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      this.voucherError = false
      this.wasVoucherValidated = true
      if (this.voucher) {
        this.validateVoucher({ voucher: this.voucher, code: this.product.code })
          .then((response) => {
            this.voucherIsValid = true
          })
          .catch((error) => {
            this.voucherError = error
          })
      } else {
        this.voucherError = 'This field is required'
      }
    },
    securityCodePopover() {
      return '<img src="images/security-code-image.jpg" />'
    },
    goToVehicleReport(vin) {
      this.$router.push({
        name: 'full-vehicle-report',
        params: { vin: vin },
      })
    },
    returningCustomer() {
      this.$ga.send(
        'event',
        'Buttons',
        'Returning Cust Checkout Login',
        'Returning Customer Checkout Login Button'
      )
    },
    newCustomer() {
      this.$ga.send(
        'event',
        'Buttons',
        'New Cust Checkout Login',
        'New Customer Checkout Login Button'
      )
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.checkout">
      <div v-if="!checkoutComplete">
        <div v-if="isLoggedIn || currentUserData" class="row">
          <div class="col-12 mt-4 mb-4">
            <h1>Checkout Now</h1>
          </div>
          <form
            :class="{ 'was-validated ': wasValidated }"
            class="needs-validation col-md-9"
            novalidate
            @submit.prevent="tryToCheckout"
          >
            <div
              v-if="!voucherIsValid && (isLoggedIn || currentUserData)"
              class="cardContainer"
            >
              <div class="cardContent">
                <b-alert v-if="checkoutError" variant="danger" show dismissible>
                  {{ checkoutError }}
                </b-alert>
                <b-alert
                  v-if="paypalCancelError"
                  variant="danger"
                  show
                  dismissible
                >
                  {{ paypalCancelError }}
                </b-alert>
                <h3>Select your payment method</h3>
                <b-form-group>
                  <b-form-radio-group
                    v-model="paymentType"
                    stacked
                    name="radiosStacked"
                  >
                    <b-form-radio class="no-validate mb-2 mt-2" value="card"
                      >Credit or Debit Card
                      <span
                        :class="$style.creditCard"
                        class="credit-card left tick"
                        >&nbsp;</span
                      ></b-form-radio
                    >
                    <b-form-radio class="no-validate" value="paypal"
                      ><img src="images/logo-paypal.png" style="width: 120px;"
                    /></b-form-radio>
                  </b-form-radio-group>
                </b-form-group>
                <div
                  v-if="voucherEnabled"
                  class="d-flex flex-row align-items-center"
                >
                  <BaseInput
                    v-model="voucher"
                    name="voucher"
                    label="Do you have a coupon code?"
                    :has-error="voucherError && wasVoucherValidated"
                  >
                    <div
                      v-if="voucherError && wasVoucherValidated"
                      class="invalid-feedback"
                      >{{ voucherError }}</div
                    >
                  </BaseInput>
                  <a href="#" class="clickLink" @click="tryToProcessVoucher"
                    >Apply code</a
                  >
                </div>
                <div v-if="voucherIsValid">
                  Yay
                </div>
                <div
                  v-if="paymentType === 'card'"
                  :class="$style.paymentContainer"
                  class="cardContent"
                >
                  <div class="row">
                    <div class="col-lg-7 col-md-12 col-12">
                      <h5>Your Billing and Delivery Information</h5>
                      <div class="row">
                        <div class="col-12">
                          <BaseInput
                            v-model="$v.firstName.$model"
                            name="firstName"
                            label="First Name"
                            :has-error="$v.firstName.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.firstName.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="!$v.firstName.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 20 characters</div
                            >
                            <div
                              v-if="!$v.firstName.minLength"
                              class="invalid-feedback"
                              >Please enter at least 2 characters</div
                            >
                          </BaseInput>
                          <BaseInput
                            v-model="$v.lastName.$model"
                            name="lastName"
                            label="Last Name"
                            :has-error="$v.lastName.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.lastName.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="!$v.lastName.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 20 characters</div
                            >
                            <div
                              v-if="!$v.lastName.minLength"
                              class="invalid-feedback"
                              >Please enter at least 2 characters</div
                            >
                          </BaseInput>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-8">
                          <BaseInput
                            v-model="$v.address.$model"
                            name="address"
                            label="Address"
                            :has-error="$v.address.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.address.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="!$v.address.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 32 characters</div
                            >
                          </BaseInput>
                        </div>
                        <div class="col-4">
                          <BaseInput
                            v-model="$v.apartmentNo.$model"
                            name="apartmentNo"
                            label="Apt No (optional)"
                            :has-error="$v.apartmentNo.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.apartmentNo.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 32 characters</div
                            >
                          </BaseInput>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-5 col-7">
                          <BaseInput
                            v-model="$v.city.$model"
                            name="city"
                            label="City"
                            :has-error="$v.city.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.city.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="!$v.city.aphaSpace && $v.city.required"
                              class="invalid-feedback"
                              >Invalid city format</div
                            >
                            <div
                              v-if="!$v.city.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 20 characters</div
                            >
                          </BaseInput>
                        </div>
                        <div class="col-md-3 col-5">
                          <label>State</label>
                          <b-form-select
                            v-model="$v.state.$model"
                            :options="stateOptions"
                            :class="{
                              'is-invalid':
                                $v.state.$model === null && wasValidated,
                            }"
                          />
                          <div
                            v-if="$v.state.$model === null && wasValidated"
                            class="invalid-feedback"
                            >Field is required</div
                          >
                        </div>
                        <div class="col-md-4 col-6">
                          <BaseInput
                            v-model="$v.zipCode.$model"
                            name="zipCode"
                            label="ZIP/Postal Code"
                            :has-error="$v.zipCode.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.zipCode.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="
                                !$v.zipCode.usaCanZipValidator &&
                                  $v.zipCode.required
                              "
                              class="invalid-feedback"
                              >Invalid ZIP/Postal code format</div
                            >
                          </BaseInput>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-5 col-md-12 col-12">
                      <h5>Secure Credit Card Payment</h5>
                      <div class="row">
                        <div class="col-md-6 col-12">
                          <label>Credit Card Type</label>
                        </div>
                        <div class="col-md-6 col-12">
                          <img
                            class="d-block w-100 cardTypes"
                            src="images/card-types.jpg"
                          />
                        </div>
                        <div class="col-12">
                          <b-form-select
                            v-model="$v.creditCardType.$model"
                            :options="creditCardTypes"
                            :class="{
                              'is-invalid':
                                $v.creditCardType.$model === null &&
                                wasValidated,
                            }"
                          />

                          <div
                            v-if="
                              $v.creditCardType.$model === null && wasValidated
                            "
                            class="invalid-feedback"
                            >Field is required</div
                          >
                        </div>

                        <div class="col-12 mt-3">
                          <BaseInput
                            v-model="$v.creditCardNumber.$model"
                            :max="maxlengthCC"
                            name="creditCardNumber"
                            label="Credit Card Number"
                            :has-error="
                              $v.creditCardNumber.$error && wasValidated
                            "
                          >
                            <div
                              v-if="!$v.creditCardNumber.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="
                                !$v.creditCardNumber.creditCardValidator &&
                                  $v.creditCardNumber.required
                              "
                              class="invalid-feedback"
                              >Please enter a valid credit card number</div
                            >
                            <div
                              v-if="
                                !$v.creditCardNumber.minLength &&
                                  $v.creditCardNumber.required &&
                                  $v.creditCardNumber.creditCardValidator
                              "
                              class="invalid-feedback"
                            >
                              Please enter at least 15 characters</div
                            >
                          </BaseInput>
                        </div>
                      </div>
                      <label>Expiration Date</label>
                      <div class="row">
                        <div class="col-7">
                          <b-form-select
                            v-model="$v.expirationMonth.$model"
                            :options="monthOptions"
                            :class="{
                              'is-invalid':
                                $v.expirationMonth.$model === null &&
                                wasValidated,
                            }"
                          />

                          <div
                            v-if="
                              $v.expirationMonth.$model === null && wasValidated
                            "
                            class="invalid-feedback"
                            >Field is required</div
                          >
                        </div>
                        <div class="col-5">
                          <b-form-select
                            v-model="$v.expirationYear.$model"
                            :options="yearOptions"
                            :class="{
                              'is-invalid':
                                $v.expirationYear.$model === null &&
                                wasValidated,
                            }"
                          />

                          <div
                            v-if="
                              $v.expirationYear.$model === null && wasValidated
                            "
                            class="invalid-feedback"
                            >Field is required</div
                          >
                        </div>
                      </div>
                      <div class="row ">
                        <div class="col-6 mt-3">
                          <BaseInput
                            v-model="$v.securityCode.$model"
                            name="securityCode"
                            type="password"
                            :max="maxlengthSC"
                            label="Security Code"
                            :has-error="$v.securityCode.$error && wasValidated"
                          >
                            <div
                              v-if="!$v.securityCode.required"
                              class="invalid-feedback"
                              >Field is required</div
                            >
                            <div
                              v-if="!$v.securityCode.minLength"
                              class="invalid-feedback"
                              >Please enter at least 3 characters</div
                            >
                            <div
                              v-if="!$v.securityCode.maxLength"
                              class="invalid-feedback"
                              >Please enter no more than 4 characters</div
                            >
                            <div
                              v-if="!$v.securityCode.numeric"
                              class="invalid-feedback"
                              >Invalid security code format</div
                            >
                          </BaseInput>
                        </div>
                        <div class="col-6 mt-3 d-flex align-items-center">
                          <span
                            v-b-popover.bottom.hover.html="securityCodePopover"
                            class="linkStyle"
                            >What's this »</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <strong>Thank you for choosing PayPal:</strong> Select Check
                  out with PayPal below to continue to PayPal and complete your
                  order. You will be returned to AutoCheck to receive your
                  report(s). Sales tax may apply.
                </div>
              </div>
            </div>
            <div v-else class="cardContainer">
              <div class="cardContent">
                <h3>Please register to access your Subscription:</h3>
                <b-alert
                  v-if="voucherCheckoutError"
                  variant="danger"
                  show
                  dismissible
                >
                  {{ voucherCheckoutError }}
                </b-alert>
                <b-alert v-if="checkoutError" variant="danger" show dismissible>
                  {{ checkoutError }}
                </b-alert>
                <b-alert v-else variant="success" show dismissible>
                  Congratulations! Your coupon code was successfully applied to
                  your order.
                </b-alert>
                <p
                  >Once registered you will receive the full report and access
                  to your VHR digital dashboard.</p
                >
                <div :class="$style.paymentContainer" class="cardContent">
                  <h5>Your Address</h5>
                  <div class="row">
                    <div class="col-8">
                      <BaseInput
                        v-model="$v.address.$model"
                        name="address"
                        label="Address"
                        :has-error="$v.address.$error && wasValidated"
                      >
                        <div
                          v-if="!$v.address.required"
                          class="invalid-feedback"
                          >Field is required</div
                        >
                      </BaseInput>
                    </div>
                    <div class="col-4">
                      <BaseInput
                        v-model="$v.apartmentNo.$model"
                        name="apartmentNo"
                        label="Apt No (optional)"
                        :has-error="$v.apartmentNo.$error && wasValidated"
                      >
                        <div
                          v-if="!$v.apartmentNo.maxLength"
                          class="invalid-feedback"
                          >Please enter no more than 32 characters</div
                        >
                      </BaseInput>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-5 col-7">
                      <BaseInput
                        v-model="$v.city.$model"
                        name="city"
                        label="City"
                        :has-error="$v.city.$error && wasValidated"
                      >
                        <div v-if="!$v.city.required" class="invalid-feedback"
                          >Field is required</div
                        >
                        <div
                          v-if="!$v.city.alpha && $v.city.required"
                          class="invalid-feedback"
                          >Invalid city format</div
                        >
                        <div v-if="!$v.city.maxLength" class="invalid-feedback"
                          >Please enter no more than 20 characters</div
                        >
                      </BaseInput>
                    </div>
                    <div class="col-md-3 col-5">
                      <label>State</label>
                      <b-form-select
                        v-model="$v.state.$model"
                        :options="stateOptions"
                        :class="{
                          'is-invalid':
                            $v.state.$model === null && wasValidated,
                        }"
                      />

                      <div
                        v-if="$v.state.$model === null && wasValidated"
                        class="invalid-feedback"
                        >Field is required</div
                      >
                    </div>
                    <div class="col-md-4 col-6">
                      <BaseInput
                        v-model="$v.zipCode.$model"
                        name="zipCode"
                        label="ZIP/Postal Code"
                        :has-error="$v.zipCode.$error && wasValidated"
                      >
                        <div
                          v-if="!$v.zipCode.required"
                          class="invalid-feedback"
                          >Field is required</div
                        >
                        <div
                          v-if="
                            !$v.zipCode.usaCanZipValidator &&
                              $v.zipCode.required
                          "
                          class="invalid-feedback"
                          >Invalid ZIP/Postal code format</div
                        >
                      </BaseInput>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-12 mb-3 mt-3">
              <input
                v-if="isLoggedIn || currentUserData"
                id="emailUpdates"
                v-model="emailUpdates"
                type="checkbox"
              />
              Yes: Please send me email updates and exclusive offers from
              AutoCheck
            </div>
            <div class="col-md-12">
              <BaseButton
                v-if="voucherIsValid"
                :disabled="tryingToCheckout"
                button-type="largeButton"
                @click="tryToCheckoutVoucher"
              >
                <BaseIcon v-if="tryingToCheckout" name="sync" spin />
                <span v-else>
                  <BaseIcon name="lock" />
                  Secure Checkout
                </span>
              </BaseButton>
              <BaseButton
                v-if="paymentType === 'card' && !voucherIsValid"
                :disabled="tryingToCheckout"
                type="submit"
                button-type="largeButton"
              >
                <BaseIcon v-if="tryingToCheckout" name="sync" spin />
                <span v-else>
                  <BaseIcon name="lock" />
                  Secure Checkout
                </span>
              </BaseButton>
              <img
                v-if="paymentType === 'paypal' && !tryingToCheckout"
                src="images/checkout-logo-large.png"
                style="cursor: pointer;"
                @click="tryToCheckoutPaypal"
              />
              <img
                v-if="paymentType === 'paypal' && tryingToCheckout"
                :disabled="tryingToCheckout"
                src="images/checkout-logo-large.png"
                style="opacity: 0.5;"
              />
              <p class="mt-3"
                >By selecting the checkout button above you agree to the
                AutoCheck.com
                <BaseLink href="https://www.experian.com/privacy/auto_check"
                  >Privacy Policy
                </BaseLink>
                and
                <span class="clickLink p-0" @click="showModal">
                  Terms and Conditions »
                </span>
              </p>
              <b-modal
                ref="termsModal"
                hide-footer
                centered
                title="Terms &amp; Conditions"
              >
                <div class="d-block">
                  <div class="content-page-inner-box"
                    ><p
                      >AutoCheck® vehicle history reports ("Reports") purchased
                      on the AutoCheck consumer site (<a
                        href="http://www.autocheck.com"
                        class="secondary-anchor-link"
                        >www.autocheck.com</a
                      >), either directly or through affiliates, are intended
                      for personal use only, and not for commercial use.
                      Commercial users may subscribe to the AutoCheck commercial
                      offering, explained in more detail at
                      <a
                        href="http://www.autocheckmembers.com."
                        class="secondary-anchor-link"
                        target="new"
                        >www.autocheckmembers.com</a
                      ></p
                    ><p
                      >"Commercial use" is defined as use by persons engaged in
                      the business of buying and/or selling of automobiles, the
                      resale or redistribution of the Reports, or any other
                      business purpose. Any attempt to retrieve AutoCheck
                      Reports via automated means will be considered commercial
                      use and therefore in violation of the terms of this
                      consumer agreement ANY COMMERCIAL USE OF THE AUTOCHECK
                      VEHICLE HISTORY REPORTS PURCHASED ON THE AUTOCHECK
                      CONSUMER SITE IS STRICTLY PROHIBITED AND ANY SUSPECTED
                      COMMERCIAL USE WILL LEAD TO THE DEACTIVATION OF YOUR
                      ACCOUNT, THE FORFEITURE OF THE RIGHT TO A REFUND OF ANY
                      MONIES ALREADY PAID TO AUTOCHECK AND MAY RESULT IN LEGAL
                      PROSECUTION. WE RESERVE THE RIGHT TO IMPOSE, AND YOU AGREE
                      TO PAY TO EXPERIAN, AN ADDITIONAL CHARGE OF $29.99 FOR
                      EACH AUTOCHECK VEHICLE HISTORY REPORT THAT EXPERIAN DEEMS
                      WAS ACCESSED FOR COMMERCIAL PURPOSES. COMMERCIAL USERS OF
                      THE AUTOCHECK CONSUMER SITE AND THEIR TRANSFEREES ARE NOT
                      ELIGIBLE FOR AND WILL NOT BE COVERED BY AUTOCHECK BUYBACK
                      PROTECTION.</p
                    ><p
                      ><strong>Notice for Consumers:</strong> You will not be
                      eligible for, nor covered by, AutoCheck Buyback Protection
                      if you obtained a Report from a dealer who obtained it
                      from the AutoCheck consumer web site. Ask your dealer to
                      provide proof of their AutoCheck commercial
                      credentials.</p
                    ><p
                      >Experian, the parent company of AutoCheck, compiles
                      Reports from extensive, multiple sources in an effort to
                      report on the history of the life of a vehicle relative to
                      its vehicle identification number (VIN). Because it is not
                      always possible for Experian to obtain complete
                      discrepancy information on all vehicles, there may be
                      other title brands, odometer readings or discrepancies
                      that apply to a vehicle that are not reflected on that
                      vehicle's report. Experian searches data from additional
                      sources where possible, but all discrepancies may not be
                      reflected on the Report.</p
                    ><p
                      >These reports are based on information supplied to
                      Experian by external sources believed to be reliable, BUT
                      NO RESPONSIBILITY IS ASSUMED BY EXPERIAN OR ITS AGENTS FOR
                      ERRORS, INACCURACIES OR OMISSIONS. THE REPORTS ARE
                      PROVIDED STRICTLY ON AN "AS IS WHERE IS" BASIS, AND
                      EXPERIAN FURTHER EXPRESSLY DISCLAIMS ALL WARRANTIES,
                      EXPRESS OR IMPLIED, INCLUDING ANY IMPLIED WARRANTIES OF
                      MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE
                      REGARDING THIS REPORT. Exceptions may be made where
                      "AutoCheck Buyback Protection" is issued and, in such
                      case, these terms and conditions may be modified only to
                      the extent inconsistent with the express terms of the
                      "AutoCheck Buyback Protection".</p
                    ><p
                      >YOU AGREE TO INDEMNIFY EXPERIAN FOR ANY CLAIMS OR LOSSES,
                      INCLUDING COSTS, EXPENSES AND ATTORNEY FEES, INCURRED BY
                      EXPERIAN ARISING DIRECTLY OR INDIRECTLY FROM YOUR IMPROPER
                      OR UNAUTHORIZED USE OF AUTOCHECK VEHICLE HISTORY
                      REPORTS.</p
                    ><p
                      >Experian shall not be liable for any delay or failure to
                      provide an accurate Report if and to the extent which such
                      delay or failure is caused by events beyond the reasonable
                      control of Experian, including, without limitation, "acts
                      of God", terrorism, or public enemies, labor disputes,
                      equipment malfunctions, material or component shortages,
                      supplier failures, embargoes, rationing, acts of local,
                      state or national governments, or public agencies, utility
                      or communication failures or delays, fire, earthquakes,
                      flood, epidemics, riots and strikes.</p
                    ><p
                      >The general used car buying information is a free service
                      offered by Experian to the used car buying public.
                      EXPERIAN DOES NOT ACCEPT ANY RESPONSIBILITY FOR ANY INJURY
                      OR DAMAGE THAT YOU MAY CAUSE TO YOURSELF, OTHERS, OR
                      PROPERTY WHEN FOLLOWING THE ADVICE GIVEN ON THIS SITE.
                      Experian has no control over how you may use the
                      information you get from the site and does not attest to
                      the validity of any information found herein, nor does it
                      guarantee any results. Use advice found on our site at
                      your own risk. If you find something on the site that you
                      disagree with, or have any suggestions to improve our
                      site, feel free to contact
                      <a
                        href="mailto:customersupport@experian.com"
                        class="secondary-anchor-link"
                        >customersupport@experian.com</a
                      >.</p
                    ><p
                      >Any e-mail or other communication sent to AutoCheck.com
                      becomes the property of Experian. By sending any e-mail or
                      other communication, the customer consents to use of
                      his/her first name and communication (written or
                      otherwise)for advertising and promotional purposes in
                      connection with AutoCheck.com without additional
                      compensation, unless prohibited by law.</p
                    ><p
                      ><strong>Coupon Terms and Conditions:</strong> Promotion
                      codes are valid for a limited time only and subject to all
                      restrictions set forth in the offer. Experian reserves the
                      right to cancel or modify the offer at any time. The
                      promotion code may not be combined with other offers. The
                      promotion code is not transferable and may not be resold.
                      The promotion code can only be used once unless specified
                      otherwise in the offer. If you violate the AutoCheck Terms
                      and Conditions, the promotion will be invalid, and the
                      promotional code discount will not apply.</p
                    ><p
                      >These terms and the relationship between you and Experian
                      shall be governed by the laws of the State of Illinois
                      (USA) without regard to its conflict of law provisions.
                      You and Experian agree to submit to the personal and
                      exclusive jurisdiction of the courts located within the
                      county of Cook, Illinois.</p
                    ></div
                  >
                </div>
              </b-modal>
            </div>
          </form>
          <div class="col-md-3">
            <CheckoutCart
              :product="product"
              :voucher-enabled="voucherIsValid"
            />
          </div>
        </div>
        <div v-else class="row">
          <div class="col-12 col-md-9 mt-4 mb-4">
            <h1>Sign In or Create a New Account</h1>
          </div>
          <div v-if="activeVinSearch" class="col-md-9 col-12 mb-4">
            <VehicleSearchSummary :cart="true" />
          </div>
          <div class="col-md-3 col-12"></div>
          <div class="col-md-9 col-12">
            <div class="cardContainer">
              <div class="cardContent">
                <h3>Tell us about yourself:</h3>
                <b-form-group>
                  <b-form-radio-group
                    v-model="customerType"
                    stacked
                    name="radiosStacked"
                  >
                    <b-form-radio
                      value="new"
                      class="mb-2 mt-2"
                      @click="newCustomer"
                      ><strong>New Customer?</strong> We'll need a few details
                      to get started:</b-form-radio
                    >
                    <b-form-radio value="returning" @click="returningCustomer"
                      ><strong>Returning Customer?</strong> Please sign in
                      now:</b-form-radio
                    >
                  </b-form-radio-group>
                </b-form-group>
                <div v-if="customerType === 'new'">
                  <UserRegistration :from-checkout="true" />
                </div>
                <div v-else>
                  <UserLogin :from-checkout="true" />
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <CheckoutCart :product="product" />
          </div>
        </div>
      </div>
      <div v-else class="row">
        <div class="col-12 mt-4">
          <h2>Welcome!</h2>
        </div>
        <div class="col-md-8 col-12 mt-4 mb-4">
          <div class="cardContainer">
            <div class="cardContent">
              <p
                >Thank you for choosing AutoCheck: Scroll down for your order
                confirmation.</p
              >
              <p v-if="activeVinSearch">
                Your subscription includes your last search: Click on "View
                Report" below to get the full report now.
              </p>
              <BaseLink v-if="!activeVinSearch" :to="{ name: 'dashboard' }">
                <BaseButton button-type="buttonPrimary">
                  <span>Go to My Account</span>
                </BaseButton>
              </BaseLink>
              <BaseButton
                v-else
                button-type="buttonPrimary"
                @click="goToVehicleReport(activeVinSearch)"
              >
                <span>View Report</span>
              </BaseButton>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-12 mt-4 mb-4">
          <!--<liveChat :is-link="false" />-->
        </div>
        <div class="col-md-8 col-12 mb-3">
          <h3
            >Thank you for choosing AutoCheck: Your order number is
            {{ checkoutResponse.transactionId }}</h3
          >
        </div>
        <div class="col-md-4 col-12 mb-3"> </div>
        <div v-if="activeVinSearch" class="col-12 mb-4">
          <VehicleSearchSummary :cart="true" />
        </div>

        <div class="col-md-8 col-12 d-flex">
          <div
            class="cardContainer productPanel mb-0 w-100"
            :class="$style.product"
          >
            <div class="row">
              <div class="col-md-4 col-12">
                <img :src="checkoutResponse.image" />
              </div>
              <div
                class="col-md-8 col-12 productDescription"
                :class="$style.productDesc"
              >
                <span>${{ checkoutResponse.price }}</span>
                <div v-html="checkoutResponse.htmlDescription"></div>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-md-4 d-flex"> </div>
              <div class="col-md-8" :class="$style.paymentSummary">
                <ul>
                  <li
                    ><div class="content-offer">Digital dashboard delivery</div
                    ><span>FREE</span></li
                  >
                  <li
                    ><div class="content-offer"
                      >Buyback Protection for qualified, registered
                      vehicles</div
                    ><span>FREE</span></li
                  >
                  <li
                    ><div class="content-offer">Local sales tax</div
                    ><span>${{ checkoutResponse.tax }}</span></li
                  >
                  <li v-if="voucherCheckoutComplete" :class="$style.discount"
                    ><div class="content-offer">Coupon code applied</div
                    ><span>- ${{ checkoutResponse.price }}</span></li
                  >
                </ul>
              </div>
            </div>
            <hr />
            <div class="row">
              <div class="col-md-4 d-flex">
                <div
                  v-if="checkoutResponse.voucher"
                  :class="$style.couponText"
                  class="mb-3"
                >
                  Coupon code: {{ checkoutResponse.voucher }}
                </div>
              </div>
              <div class="col-md-8" :class="$style.paymentSummary">
                <ul>
                  <li :class="$style.orderTotal"
                    ><div class="content-offer mr-2">Total</div>
                    <span>${{ checkoutResponse.orderTotal }}</span></li
                  >
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-12 d-flex">
          <div class="cardContainer">
            <h4>Payment Details</h4>
            <p
              >Thank you for your order. Because your security is important to
              us we will not save your credit card or PayPal information once
              you have completed your transaction.</p
            >
            <div :class="$style.payment" class="mt-4">
              <p v-if="!voucherCheckoutComplete"
                ><strong>Secure Credit Card Payment</strong>
                <span :class="$style.creditCard" class="credit-card left tick"
                  >&nbsp;</span
                ></p
              >
              <p v-else><strong>Name</strong></p>
              <p
                >{{ checkoutResponse.firstName }}
                {{ checkoutResponse.lastName }}</p
              >
              <p v-if="!voucherCheckoutComplete">
                {{ checkoutResponse.creditCardType }} card ending in
                {{ checkoutResponse.creditCardNumber }}</p
              >
              <p v-if="!voucherCheckoutComplete"
                >Expires {{ checkoutResponse.expirationMonth }}/{{
                  checkoutResponse.expirationYear
                }}</p
              >
              <p v-if="!voucherCheckoutComplete" class="mt-3"
                ><strong>Billing Address</strong></p
              >
              <p v-else class="mt-3"><strong>Address</strong></p>
              <p
                >{{ checkoutResponse.address }},
                <span v-if="checkoutResponse.apartmentNo"
                  >{{ checkoutResponse.apartmentNo }},</span
                ></p
              >
              <p>{{ checkoutResponse.city }}, {{ checkoutResponse.state }}</p>
              <p>{{ checkoutResponse.zipCode }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.paymentContainer {
  background: #fff;
}
.payment {
  p {
    margin-bottom: 5px;
  }
  strong {
    margin-top: 10px;
  }
}
.paymentSummary {
  ul {
    padding-left: 0;
    list-style: none;
    li {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 3px 0;
    }
  }
}
.productDesc {
  position: relative;
  > span {
    position: absolute;
    top: 15px;
    right: 15px;
    font-size: 20px;
  }
  @media (max-width: 768px) {
    > span {
      top: 30px;
      font-size: 20px !important;
    }
    h5 {
      font-size: 15px !important;
    }
  }
}
.orderTotal {
  justify-content: flex-end !important;
  font-size: 20px;
  font-weight: bold;
}
.voucherText {
  display: flex;
  margin-top: auto;
  margin-bottom: 1rem;
}

.discount {
  color: #49a942;
}
.creditCard {
  display: inline-block;
  width: 34px;
  height: 25px;
  margin-right: 0;
  margin-left: 5px;
  background: url(@assets/images/sprite_v1.png) no-repeat scroll -388px -497px;
}
.paypalType {
  display: inline-block;
  width: 120px;
  height: 25px;
  margin-right: 4px;
  margin-left: 11px;
  background: url(@assets/images/sprite_v1.png) no-repeat scroll -362px -525px;
}
</style>
